import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface SearchMatch {
  pageIndex: number; // 0-based
  itemIndex: number; // index of the text item within the page
  occurrenceInItem: number; // which occurrence within that text item (0-based)
}

interface PDFSearchResult {
  searchText: string;
  setSearchText: (text: string) => void;
  matches: SearchMatch[];
  currentMatchIndex: number;
  currentMatch: SearchMatch | null;
  goToNextMatch: () => void;
  goToPreviousMatch: () => void;
  currentMatchPage: number | null; // 1-based page number
  isExtracting: boolean;
}

// Each page stores an array of individual text item strings
type PageTextItems = string[][];

function usePDFSearch(pdfDocument: PDFDocumentProxy | null, setPageNumber: (page: number) => void): PDFSearchResult {
  const [searchText, setSearchTextRaw] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [pageTextItems, setPageTextItems] = useState<PageTextItems>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedForDoc, setExtractedForDoc] = useState<PDFDocumentProxy | null>(null);

  const setSearchText = useCallback((text: string) => {
    setSearchTextRaw(text);
    setCurrentMatchIndex(0);
  }, []);

  // Extract text only when user starts searching (deferred), reusing the already-loaded document
  useEffect(() => {
    if (!pdfDocument || searchText.length < 2 || extractedForDoc === pdfDocument) {
      return;
    }

    let cancelled = false;
    const extractText = async (): Promise<void> => {
      setIsExtracting(true);
      try {
        const allPages: string[][] = [];

        for (let i = 1; i <= pdfDocument.numPages; i++) {
          const page = await pdfDocument.getPage(i);
          const textContent = await page.getTextContent();
          const items = textContent.items.filter((item) => 'str' in item).map((item) => (item as { str: string }).str);
          allPages.push(items);
        }

        if (!cancelled) {
          setPageTextItems(allPages);
          setExtractedForDoc(pdfDocument);
        }
      } catch (error) {
        console.error('Error extracting PDF text:', error);
      } finally {
        if (!cancelled) {
          setIsExtracting(false);
        }
      }
    };

    extractText();
    return () => {
      cancelled = true;
    };
  }, [pdfDocument, searchText, extractedForDoc]);

  // Reset extracted text when document changes
  useEffect(() => {
    if (pdfDocument !== extractedForDoc) {
      setPageTextItems([]);
      setExtractedForDoc(null);
    }
  }, [pdfDocument, extractedForDoc]);

  // Find matches within individual text items (same unit customTextRenderer sees)
  const matches = useMemo(() => {
    if (!searchText || searchText.length < 2 || pageTextItems.length === 0) {
      return [];
    }

    const result: SearchMatch[] = [];
    const query = searchText.toLowerCase();

    pageTextItems.forEach((items, pageIndex) => {
      items.forEach((itemText, itemIndex) => {
        const lowerText = itemText.toLowerCase();
        let startPos = 0;
        let occurrenceInItem = 0;

        while (true) {
          const foundAt = lowerText.indexOf(query, startPos);
          if (foundAt === -1) break;
          result.push({ pageIndex, itemIndex, occurrenceInItem });
          occurrenceInItem++;
          startPos = foundAt + 1;
        }
      });
    });

    return result;
  }, [searchText, pageTextItems]);

  // Navigate to the page of the current match
  const currentMatch = matches[currentMatchIndex] ?? null;
  const currentMatchPage = currentMatch ? currentMatch.pageIndex + 1 : null;

  useEffect(() => {
    if (currentMatchPage !== null) {
      setPageNumber(currentMatchPage);
    }
  }, [currentMatchPage, setPageNumber]);

  const goToNextMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matches.length);
  }, [matches.length]);

  const goToPreviousMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
  }, [matches.length]);

  return {
    searchText,
    setSearchText,
    matches,
    currentMatchIndex,
    currentMatch,
    goToNextMatch,
    goToPreviousMatch,
    currentMatchPage,
    isExtracting,
  };
}

export default usePDFSearch;
